//frontend/src/App.tsx

import React, { useCallback, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import Home from './pages/landing/Home';
import { AuthProvider, useAuth } from "./context/AuthContext";
import AccessibilityProvider from './components/shared/AccessibilityProvider';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { ThemeProvider } from './context/ThemeContext';
import { SettingsProvider } from './context/SettingsContext';
import ErrorBoundary from './components/shared/ErrorBoundary';
import analytics from './utils/analytics';
import errorMonitoring from './utils/errorMonitoring';
import RouteLoadingState from './components/routes/RouteLoadingState';
import { useCommandPalette, useKeyboardShortcuts } from './hooks/useGlobalShortcutDialogs';
import GlobalAssistant from './components/shared/GlobalAssistant';
import { AssistantPageDraftProvider } from './context/AssistantPageDraftContext';
import { Toaster } from 'react-hot-toast';
import { OfflineIndicator } from './components/shared/OfflineIndicator';
import { CookieConsent } from './components/CookieConsent';
import RouteSEO from './components/marketing/RouteSEO';
import MarketingFunnelTracker from './components/marketing/MarketingFunnelTracker';
import { useConsent } from './hooks/useConsent';
import { isSensitiveCredentialRoute } from './utils/sensitiveRoutePrivacy';
import ProgramAccessBanner from './components/shared/ProgramAccessBanner';
import PaidDowngradeRecoveryBanner from './components/shared/PaidDowngradeRecoveryBanner';

import TopBar from './components/layout/TopBar';
import Footer from './components/Footer';
import ScrollToTop from './components/shared/ScrollToTop';
import { useTranslation } from 'react-i18next';
import { isPlatformAdminUser } from './utils/platformAdmin';
import FounderSupportStatusBanner from './components/dashboard/FounderSupportStatusBanner';
import {
  clearFounderSupportSession,
  getFounderSupportRedirect,
  isFounderSupportView,
  readFounderSupportSession,
} from './utils/founderSupportSession';

const PrivacyAwareTelemetry: React.FC = () => {
  const { pathname } = useLocation();
  const { state, hasConsent } = useConsent();

  useEffect(() => {
    if (isSensitiveCredentialRoute(pathname)) return;
    if (hasConsent('analytics')) {
      analytics.init();
      errorMonitoring.init();
    }
  }, [hasConsent, pathname, state]);

  return null;
};

// ✨ LAZY LOADED PAGES - Code splitting for optimal bundle size
// Keep the landing first viewport immediate; load every secondary page at its route boundary.
const Login = lazy(() => import('./pages/auth/Login'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const GetStarted = lazy(() => import('./pages/landing/GetStarted'));
const FAQPage = lazy(() => import('./pages/landing/FAQPage'));
const Reviews = lazy(() => import('./pages/landing/Reviews'));
const HowItWorksPage = lazy(() => import('./pages/landing/HowItWorksPage'));
const Report = lazy(() => import('./pages/landing/Report'));
const FeaturesPage = lazy(() => import('./pages/landing/FeaturesPage'));
const PricingPageUltimate = lazy(() => import('./pages/landing/PricingPageUltimate'));
const ProgramsPage = lazy(() => import('./pages/landing/ProgramsPage'));
const HelpCenterPage = lazy(() => import('./pages/landing/HelpCenterPage'));
const ReferralPage = lazy(() => import('./pages/community/ReferralPage'));
const PublicReportPage = lazy(() => import('./pages/PublicReportPage'));
const UnifiedDashboard = lazy(() => import('./pages/dashboard/UnifiedDashboard'));
const AdminDashboard = lazy(() => import('./pages/dashboard/AdminDashboard'));
const AdminReportDetailPage = lazy(() => import('./pages/admin/AdminReportDetailPage'));
const GodEyePage = lazy(() => import('./pages/admin/GodEyePage'));
// BOSS v3 Phase 0 — S11 admin feature-flag management
const AdminFeatureFlags = lazy(() => import('./pages/admin/AdminFeatureFlags'));
// BOSS v3 Phase 0 — S04 explainer page (linked from RecalibrationBanner)
const V3ChangesPage = lazy(() => import('./pages/boss/V3ChangesPage'));
const SmartOnboarding = lazy(() => import('./pages/landing/SmartOnboarding'));
const TriviaGame = lazy(() => import('./pages/games/triviaGame'));
const SharedQuizPlayer = lazy(() => import('./pages/games/SharedQuizPlayer'));
const Wayfinder = lazy(() => import('./pages/tools/Wayfinder'));
const ImpactEvaluator = lazy(() => import('./pages/tools/ImpactEvaluator'));
const SafeguardWorkflowPage = lazy(() => import('./pages/tools/SafeguardWorkflow'));
const GeoWorkflowPage = lazy(() => import('./pages/tools/GeoWorkflowPage'));
const GeoAnalyzer = lazy(() => import('./pages/tools/GeoAnalyzer'));
const SavedBrandWatchReport = lazy(() => import('./pages/tools/SavedBrandWatchReport'));
const ImpactWizard = lazy(() => import('./pages/tools/ImpactWizard'));
const OpportunityDashboard = lazy(() => import('./pages/dashboard/OpportunityDashboard'));
const FounderArtifactPage = lazy(() => import('./pages/dashboard/FounderArtifactPage'));
const MarketOpportunitiesPage = lazy(() => import('./pages/dashboard/MarketOpportunitiesPage'));
const GoToMarketPage = lazy(() => import('./pages/dashboard/GoToMarketPage'));
const RisksCompliancePage = lazy(() => import('./pages/dashboard/RisksCompliancePage'));
const ActionsRoadmapPage = lazy(() => import('./pages/dashboard/ActionsRoadmapPage'));
const FundingResourcesPage = lazy(() => import('./pages/dashboard/FundingResourcesPage'));
const BountyBoard = lazy(() => import('./pages/bounties/BountyBoard'));
const BountyCreation = lazy(() => import('./pages/bounties/BountyCreation'));
const BountyDetail = lazy(() => import('./pages/bounties/BountyDetail'));
const HunterDashboard = lazy(() => import('./pages/bounties/HunterDashboard'));
const FeatureVotingPage = lazy(() => import('./pages/landing/FeatureVoting'));
const GamificationHub = lazy(() => import('./pages/gamification/GamificationHub'));
const AchievementsPage = lazy(() => import('./pages/gamification/AchievementsPage'));
const AnalyticsDashboard = lazy(() => import('./pages/gamification/AnalyticsDashboard'));
const EnhancedLeaderboard = lazy(() => import('./pages/dashboard/EnhancedLeaderboard'));
const EnhancedProfile = lazy(() => import('./pages/dashboard/EnhancedProfile'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const I18nTest = lazy(() => import('./pages/test/I18nTest'));
const GameChanger = lazy(() => import('./pages/games/GameChanger'));

const PreservedRouteAlias: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: to, search: location.search }}
      state={location.state}
      replace
    />
  );
};

const SettingsUsageAlias: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  searchParams.set('tab', 'usage');

  return (
    <Navigate
      to={{ pathname: '/settings', search: `?${searchParams.toString()}` }}
      state={location.state}
      replace
    />
  );
};
const AIrMiniGame = lazy(() => import('./pages/games/AIrMiniGame'));
const SustainabilityQuiz = lazy(() => import('./pages/games/SustainabilityQuiz'));
const DocumentsPage = lazy(() => import('./pages/settings/DocumentsPage'));
const ProgramWorkspacePage = lazy(() => import('./pages/programs/ProgramWorkspacePage'));
const AdminTopBar = lazy(() => import('./components/layout/AdminTopBar'));
const UserTopBar = lazy(() => import('./components/layout/UserTopBar'));
const CommandPalette = lazy(() => import('./components/shared/CommandPalette'));
const KeyboardShortcuts = lazy(() => import('./components/shared/KeyboardShortcuts'));
const PersonaRoute = lazy(() => import('./components/routes/PersonaRoute'));
const EntitlementRoute = lazy(() => import('./components/routes/EntitlementRoute'));
const DashboardEntitlementRoute = lazy(() => import('./components/routes/DashboardEntitlementRoute'));
const PrivateSavedReportRoute = lazy(() => import('./components/routes/PrivateSavedReportRoute'));
const UpdateBanner = lazy(() => import('./components/shared/UpdateBanner'));

// Context-Aware TopBar Component - Uses Wix Roles
const ContextAwareTopBar: React.FC = () => {
  const { user, isLoggedIn } = useAuth();
  const { pathname } = useLocation();
  const supportSession = readFounderSupportSession();

  if (pathname.startsWith('/trivia/embed/')) {
    return null;
  }

  // Keep the authenticated Platform Admin identity out of Founder chrome.
  // The signed banner names both parties; this shell offers only a safe route home.
  if (supportSession && isFounderSupportView()) {
    return (
      <header className="topbar">
        <div className="topbar-container">
          <div className="topbar-left">
            <Link to="/dashboard?support=true" className="topbar-logo">
              XPROJEX
            </Link>
          </div>
        </div>
      </header>
    );
  }
  
  const hasAdminRole = isPlatformAdminUser(user);
  
  // Show AdminTopBar if user is admin (regardless of route)
  if (hasAdminRole && isLoggedIn) {
    return (
      <Suspense fallback={<TopBar />}>
        <AdminTopBar />
      </Suspense>
    );
  }
  
  // Show UserTopBar for logged-in non-admin users
  if (isLoggedIn) {
    return (
      <Suspense fallback={<TopBar />}>
        <UserTopBar />
      </Suspense>
    );
  }
  
  // Show default TopBar for public pages
  return <TopBar />;
};

const HideDuringFounderSupport: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useLocation();
  return isFounderSupportView() ? null : <>{children}</>;
};

const AuthenticatedUpdateBanner: React.FC = () => {
  const { isLoggedIn } = useAuth();

  if (!isLoggedIn) return null;

  return (
    <Suspense fallback={null}>
      <UpdateBanner />
    </Suspense>
  );
};

const FounderSupportRouteBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const storedSession = readFounderSupportSession();
  const supportView = isFounderSupportView();
  const redirect = getFounderSupportRedirect(
    location.pathname,
    location.search,
    Boolean(storedSession?.id),
  );

  const exitSupportView = useCallback(() => {
    clearFounderSupportSession();
    navigate('/admin/god-eye', { replace: true });
  }, [navigate]);

  if (redirect) return <Navigate to={redirect} replace />;

  return <>
    <FounderSupportStatusBanner
      session={supportView ? storedSession : null}
      onAdminExit={exitSupportView}
    />
    {children}
  </>;
};

const APP_WORKSPACE_PATHS = [
  '/dashboard',
  '/programs/workspace',
  '/reports',
  '/artifacts',
  '/market',
  '/gtm',
  '/risks',
  '/actions',
  '/funding',
  '/admin',
  '/settings',
  '/my-documents',
  '/bounties',
  '/achievements',
  '/analytics',
  '/impact-evaluator',
  '/wayfinder',
  '/safeguard',
  '/geo',
  '/geo-analyzer',
  '/impact-wizard',
  '/trivia',
  '/ai-readiness',
  '/sustainability-quiz'
];

const ContextAwareFooter: React.FC = () => {
  const { pathname } = useLocation();
  const isWorkspaceRoute = APP_WORKSPACE_PATHS.some((path) =>
    pathname === path || pathname.startsWith(`${path}/`)
  );

  return isWorkspaceRoute ? null : <Footer />;
};

// Email Capture Wrapper - DISABLED for now
// Wix API doesn't expose user email, using contactId fallback instead
const EmailCaptureWrapper: React.FC = () => {
  return null; // Disabled - not needed with contactId fallback
};

// 🚀 Global Command Palette - Linear/Superhuman style Cmd+K
const GlobalShortcutDialogs: React.FC = () => {
  const { isOpen: cmdOpen, close: cmdClose } = useCommandPalette();
  const { isOpen: kbOpen, close: kbClose } = useKeyboardShortcuts();
  
  return (
    <Suspense fallback={null}>
      {cmdOpen ? <CommandPalette isOpen onClose={cmdClose} /> : null}
      {kbOpen ? <KeyboardShortcuts isOpen onClose={kbClose} /> : null}
    </Suspense>
  );
};

// SF-2026-01: 404 catch-all — branded, theme-aware, a11y-friendly fallback.
// Inline (no new file) to keep the diff surface small. Uses semantic HTML +
// CSS variables from theme.css so dark mode + reduced-motion are inherited.
const NotFound: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  return (
    <section
      role="alert"
      aria-labelledby="not-found-title"
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        textAlign: 'center',
        color: 'var(--text-primary, #111)',
      }}
    >
      <div style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1, marginBottom: '0.5rem', color: 'var(--accent-primary, #4f46e5)' }}>404</div>
      <h1 id="not-found-title" style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem' }}>
        {t('autogen.bc3023b3')}
      </h1>
      <p style={{ maxWidth: '32rem', color: 'var(--text-secondary, #555)', marginBottom: '1.5rem' }}>
        {t('autogen.e9bb847b')} <code style={{ background: 'var(--bg-secondary, #f3f4f6)', padding: '0.1rem 0.35rem', borderRadius: '0.25rem' }}>{location.pathname}</code>{t('autogen.24c2d168')}
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        <a
          href="/"
          style={{
            padding: '0.6rem 1.25rem',
            borderRadius: '0.5rem',
            background: 'var(--accent-primary, #4f46e5)',
            color: '#fff',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          {t('autogen.f36ae945')}
        </a>
        <a
          href="/dashboard"
          style={{
            padding: '0.6rem 1.25rem',
            borderRadius: '0.5rem',
            background: 'transparent',
            color: 'var(--text-primary, #111)',
            border: '1px solid var(--border-primary, #d1d5db)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          {t('autogen.7ad1caed')}
        </a>
      </div>
    </section>
  );
};

const App: React.FC = () => {
  const { t } = useTranslation();

    return (
      <ErrorBoundary>
        <I18nextProvider i18n={i18n}>
          <ThemeProvider>
            <AccessibilityProvider>
              <Router>
                <PrivacyAwareTelemetry />
                <RouteSEO />
                <MarketingFunnelTracker />
                {/* 🔝 GLOBAL SCROLL-TO-TOP: Fixes footer navigation issue (Dec 18, 2025) */}
                <ScrollToTop />
                {/* ✨ PREMIUM UPDATE BANNER: Linear/Notion style update notification (Dec 19, 2025) */}
                <AuthProvider>
                <SettingsProvider>
                <AssistantPageDraftProvider>
              {/* Toast Notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: 'var(--card-bg)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                    padding: '16px',
                    fontSize: '15px',
                    fontWeight: 500,
                  },
                  success: {
                    iconTheme: {
                      primary: 'var(--success-500)',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: 'var(--error-500)',
                      secondary: '#fff',
                    },
                    duration: 5000,
                  },
                }}
              />
              {/* MOB-06: Offline/Reconnected indicator */}
              <OfflineIndicator />
              {/* 🍪 PRIVACY-CONSENT-01: Cookie consent banner (Loi 25 + GDPR Art. 7 + ePrivacy) */}
              <FounderSupportRouteBoundary>
              <HideDuringFounderSupport><AuthenticatedUpdateBanner /></HideDuringFounderSupport>
              {/* ✨ Context-Aware TopBar: Shows Admin or User TopBar based on route */}
              <ContextAwareTopBar />
              <HideDuringFounderSupport><ProgramAccessBanner /></HideDuringFounderSupport>
              <HideDuringFounderSupport><PaidDowngradeRecoveryBanner /></HideDuringFounderSupport>
              
              {/* 🚀 LINEAR/SUPERHUMAN: Global Command Palette (Cmd+K) */}
              <HideDuringFounderSupport><GlobalShortcutDialogs /></HideDuringFounderSupport>
              
              <GlobalAssistant />
              
              {/* ✨ Email Capture Banner: Shows when user needs to provide email */}
              <HideDuringFounderSupport><EmailCaptureWrapper /></HideDuringFounderSupport>
              <HideDuringFounderSupport><CookieConsent /></HideDuringFounderSupport>
            
            {/* Main content area - target for skip link accessibility */}
            <main id="main-content" role="main" tabIndex={-1} aria-label={t('autogen.6e090400')} style={{ outline: 'none' }}>
            <Suspense fallback={<RouteLoadingState label={t('routeLoadingTitle', 'Getting this view ready...')} />}>
            <Routes>
                <Route path="/" element={<ErrorBoundary><Home /></ErrorBoundary>} />
                <Route path="/login" element={<ErrorBoundary><Login /></ErrorBoundary>} />
                <Route path="/signup" element={<ErrorBoundary><Signup /></ErrorBoundary>} />
                {/* Founder-first entries: discover an idea, or validate one directly. */}
                <Route path="/get-started" element={<ErrorBoundary><SmartOnboarding /></ErrorBoundary>} />
                <Route path="/concept-validator" element={<ErrorBoundary><GetStarted /></ErrorBoundary>} />
                
                {/* ✨ PUBLIC: Shared Report Viewing (no auth required) */}
                <Route path="/shared/report/:shareToken" element={<ErrorBoundary><PublicReportPage /></ErrorBoundary>} />
                <Route path="/report" element={<ErrorBoundary><Report /></ErrorBoundary>} />
                <Route path="/report/:id" element={<PrivateSavedReportRoute><ErrorBoundary><Report /></ErrorBoundary></PrivateSavedReportRoute>} />
                <Route path="/artifacts/:artifactId" element={<PrivateSavedReportRoute><ErrorBoundary><FounderArtifactPage /></ErrorBoundary></PrivateSavedReportRoute>} />
                <Route path="/faqs" element={<ErrorBoundary><FAQPage /></ErrorBoundary>} />
                <Route path="/reviews" element={<ErrorBoundary><Reviews /></ErrorBoundary>} />
                <Route path="/how-it-works" element={<ErrorBoundary><HowItWorksPage /></ErrorBoundary>} />
                <Route path="/help-center" element={<ErrorBoundary><HelpCenterPage /></ErrorBoundary>} />
                <Route path="/help" element={<Navigate to="/help-center" replace />} />
                <Route path="/features" element={<ErrorBoundary><FeaturesPage /></ErrorBoundary>} />
                <Route path="/tools" element={<ErrorBoundary><FeaturesPage /></ErrorBoundary>} />
		        {/* ✨ Ultimate Pricing Page - Combined all features */}
                <Route path="/pricing" element={<ErrorBoundary><PricingPageUltimate /></ErrorBoundary>} />
                
                {/* ✨ NEW: Programs/Institutional Page */}
                <Route path="/programs" element={<ErrorBoundary><ProgramsPage /></ErrorBoundary>} />
                <Route path="/programs/workspace" element={<ErrorBoundary><ProgramWorkspacePage /></ErrorBoundary>} />
                
                {/* Historical aliases preserve old links without creating parallel product flows. */}
                <Route path="/demo" element={<PreservedRouteAlias to="/concept-validator" />} />
                <Route path="/onboarding" element={<PreservedRouteAlias to="/get-started" />} />
                
                {/* ✨ UNIFIED DASHBOARD - Primary workspace entry point */}
                <Route path="/dashboard" element={<DashboardEntitlementRoute><UnifiedDashboard /></DashboardEntitlementRoute>} />
                
                {/* Compatibility path into the primary dashboard journey view. */}
                {/* Row 97 MY-JOURNEY-REDIRECT-FIX-01 (2026-05-25): was /dashboard?tab=journey */}
                {/* but that hit the DashboardLockedView for non-earned users → loop. */}
                {/* /journey keeps the dashboard progress path as a redirect into the primary workspace. */}
                <Route path="/my-journey" element={<Navigate to="/journey" replace />} />
                
                {/* ✨ NEW: Venture Journey & Bounty Hunters */}
                <Route path="/journey" element={<Navigate to="/dashboard?tab=journey" replace />} />
                <Route path="/bounties" element={<EntitlementRoute featureKey="bounties"><BountyBoard /></EntitlementRoute>} />
                <Route path="/bounties/features" element={<EntitlementRoute featureKey="bounties"><FeatureVotingPage /></EntitlementRoute>} />
                <Route path="/bounties/create" element={<EntitlementRoute featureKey="bounties"><BountyCreation /></EntitlementRoute>} />
                <Route path="/bounties/my-dashboard" element={<EntitlementRoute featureKey="bounties"><HunterDashboard /></EntitlementRoute>} />
                <Route path="/bounties/:bountyId" element={<EntitlementRoute featureKey="bounties"><BountyDetail /></EntitlementRoute>} />
                
                {/* ✨ NEW Sprint 4-5: Business Intelligence Pages */}
                {/* Sub-views of /dashboard (InsightsWorkspace); access is controlled by the dashboard entitlement. */}
                <Route path="/market" element={<DashboardEntitlementRoute><MarketOpportunitiesPage /></DashboardEntitlementRoute>} />
                <Route path="/gtm" element={<DashboardEntitlementRoute><GoToMarketPage /></DashboardEntitlementRoute>} />
                <Route path="/risks" element={<DashboardEntitlementRoute><RisksCompliancePage /></DashboardEntitlementRoute>} />
                <Route path="/actions" element={<DashboardEntitlementRoute><ActionsRoadmapPage /></DashboardEntitlementRoute>} />
                <Route path="/funding" element={<DashboardEntitlementRoute><FundingResourcesPage /></DashboardEntitlementRoute>} />
                
                {/* ✨ REPORTS PAGE - Full reports listing and management */}
                <Route path="/reports" element={<EntitlementRoute featureKey="reports" mode="preview-or-full"><OpportunityDashboard /></EntitlementRoute>} />
                
                {/* Legacy dashboard redirects for backward compatibility */}
                <Route path="/opportunities" element={<Navigate to="/reports" replace />} />
                <Route path="/portfolio" element={<Navigate to="/dashboard?tab=portfolio" replace />} />
                
                {/* ✨ GAMIFICATION ZONE - Separate achievement tracking area */}
                <Route path="/achievements" element={<EntitlementRoute featureKey="achievements"><GamificationHub /></EntitlementRoute>} />
                <Route path="/achievements/details" element={<EntitlementRoute featureKey="achievements"><AchievementsPage /></EntitlementRoute>} />
                <Route path="/leaderboard/enhanced" element={<EntitlementRoute featureKey="achievements"><EnhancedLeaderboard /></EntitlementRoute>} />
                <Route path="/profile/enhanced" element={<EntitlementRoute featureKey="profile"><EnhancedProfile /></EntitlementRoute>} />
                <Route path="/analytics" element={<EntitlementRoute featureKey="analytics"><AnalyticsDashboard /></EntitlementRoute>} />
                
                {/* Legacy gamification redirects */}
                <Route path="/leaderboard" element={<Navigate to="/achievements?tab=leaderboard" replace />} />

                {/* /community redirects to dashboard Growth tab */}
                <Route path="/community" element={<Navigate to="/dashboard?tab=community" replace />} />

                {/* Referral program */}
                <Route path="/referral" element={<ErrorBoundary><ReferralPage /></ErrorBoundary>} />
                
                {/* Admin panel (requires admin role) */}
                <Route path="/admin" element={<PersonaRoute requiredPlan="admin"><AdminDashboard /></PersonaRoute>} />
                <Route path="/admin/reports/:id" element={<PersonaRoute requiredPlan="admin"><AdminReportDetailPage /></PersonaRoute>} />
                <Route path="/admin/god-eye" element={<PersonaRoute requiredPlan="admin"><GodEyePage /></PersonaRoute>} />
                <Route path="/admin/feature-flags" element={<PersonaRoute requiredPlan="admin"><AdminFeatureFlags /></PersonaRoute>} />
                {/* BOSS v3 Phase 0 — S04: explainer page linked from the RecalibrationBanner CTA. */}
                <Route path="/boss/v3-changes" element={<V3ChangesPage />} />
                
                {/* ✨ VALIDATION TOOLS - DB-aware feature access with preview support */}
                <Route path="/impact-evaluator" element={<EntitlementRoute featureKey="impact_evaluator" mode="preview-or-full"><ImpactEvaluator /></EntitlementRoute>} />
                <Route path="/impact-evaluator/:id" element={<PrivateSavedReportRoute><EntitlementRoute featureKey="impact_evaluator" mode="preview-or-full"><ImpactEvaluator /></EntitlementRoute></PrivateSavedReportRoute>} />
                <Route path="/wayfinder" element={<EntitlementRoute featureKey="wayfinder" mode="preview-or-full"><Wayfinder /></EntitlementRoute>} />
                <Route path="/Wayfinder" element={<Navigate to="/wayfinder" replace />} />
                <Route path="/wayfinder/:id" element={<PrivateSavedReportRoute><EntitlementRoute featureKey="wayfinder" mode="preview-or-full"><Wayfinder /></EntitlementRoute></PrivateSavedReportRoute>} />
                <Route path="/safeguard" element={<EntitlementRoute featureKey="safeguard"><SafeguardWorkflowPage /></EntitlementRoute>} />
                <Route path="/safeguard/:id" element={<PrivateSavedReportRoute><EntitlementRoute featureKey="safeguard"><SafeguardWorkflowPage /></EntitlementRoute></PrivateSavedReportRoute>} />
                <Route path="/geo" element={<EntitlementRoute featureKey="brand_watch"><GeoWorkflowPage /></EntitlementRoute>} />
                <Route path="/geo/:id" element={<PrivateSavedReportRoute><ErrorBoundary><SavedBrandWatchReport /></ErrorBoundary></PrivateSavedReportRoute>} />
                <Route path="/geo-analyzer" element={<EntitlementRoute featureKey="brand_watch"><GeoAnalyzer /></EntitlementRoute>} />
                <Route path="/impact-wizard" element={<EntitlementRoute featureKey="impact_engine"><ImpactWizard /></EntitlementRoute>} />
                
                {/* ✨ GAMIFICATION GAMES - Interactive learning (accessible from gamification zone) */}
                {/* /game-changer hub is always public — individual sub-tools gate themselves */}
                <Route path="/game-changer" element={<GameChanger />} />
                <Route path="/trivia" element={<EntitlementRoute featureKey="trivia"><TriviaGame /></EntitlementRoute>} />
                <Route path="/triviaGame" element={<PreservedRouteAlias to="/trivia" />} />
                <Route path="/trivia/play/:id" element={<ErrorBoundary><SharedQuizPlayer /></ErrorBoundary>} />
                <Route path="/trivia/embed/:id" element={<ErrorBoundary><SharedQuizPlayer embedded /></ErrorBoundary>} />
                <Route path="/ai-readiness" element={<EntitlementRoute featureKey="ai_readiness"><AIrMiniGame /></EntitlementRoute>} />
                <Route path="/sustainability-quiz" element={<EntitlementRoute featureKey="sustainability_quiz"><SustainabilityQuiz /></EntitlementRoute>} />
                
                {/* ✨ USER PROFILE & SETTINGS - Unified in one place */}
                <Route path="/profile" element={<Navigate to="/settings?tab=profile" replace />} />
                <Route path="/settings/usage" element={<SettingsUsageAlias />} />
                <Route path="/my-documents" element={<DocumentsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                
                {/* 🧪 TEST PAGES - Development only */}
                <Route path="/test/i18n" element={<I18nTest />} />

                {/* SF-2026-01: 404 catch-all — branded fallback for unknown URLs */}
                <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </main>

            {/* Public footer only; app workspaces keep the founder/admin surface focused. */}
            <HideDuringFounderSupport><ContextAwareFooter /></HideDuringFounderSupport>
              </FounderSupportRouteBoundary>
          </AssistantPageDraftProvider>
          </SettingsProvider>
          </AuthProvider>  
        </Router>
      </AccessibilityProvider>
      </ThemeProvider>
    </I18nextProvider>
    </ErrorBoundary>
    );
};

export default App;
